/**
 * Alertas por correo (rutas del bloque 6b, `/api/notifications`). Reescrito para H3.2 (13-10): la versión
 * anterior leía un token de localStorage que no existe y nunca mandaba la sesión (A-08); ahora usa la sesión de
 * Supabase como el resto de los servicios.
 */
import axios from 'axios';
import { supabase } from '../utils/supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface AlertSubscription {
  id: string;
  user_id: string;
  email: string;
  enabled: boolean;
  frequency: 'daily';
  last_notification_sent: string | null;
  created_at: string;
  updated_at: string;
}

/** Si el resumen diario y la prueba salen de verdad (lo decide el backend según el transporte y el cron). */
export interface DeliveryStatus {
  digest_enabled: boolean;
  test_available: boolean;
  /** Remitente real del transporte; solo viene cuando el transporte puede enviar. */
  from?: string | null;
}

export interface NotificationHistoryItem {
  id: string;
  subject: string;
  sent_at: string;
  status: 'sent' | 'failed' | 'pending';
  metadata: { document_count?: number } | null;
}

export interface SubscriptionState {
  subscription: AlertSubscription | null;
  delivery: DeliveryStatus;
}

/** Resultado de «Enviar prueba» en palabras de la pantalla. */
export type TestResult = 'enviado' | 'envio_inactivo' | 'sin_suscripcion' | 'sin_documentos' | 'error';

const authHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
};

const DELIVERY_OFF: DeliveryStatus = { digest_enabled: false, test_available: false };

export const notificationsService = {
  async getSubscription(): Promise<SubscriptionState> {
    const headers = await authHeaders();
    const { data } = await axios.get(`${API_URL}/notifications/subscription`, { headers });
    return { subscription: data.data || null, delivery: data.delivery || DELIVERY_OFF };
  },

  /** Crea o actualiza la suscripción (la frecuencia es siempre diaria). */
  async setEnabled(enabled: boolean): Promise<AlertSubscription> {
    const headers = await authHeaders();
    const { data } = await axios.post(`${API_URL}/notifications/subscription`, { enabled }, { headers });
    return data.data;
  },

  /** Baja: borra la suscripción. */
  async unsubscribe(): Promise<void> {
    const headers = await authHeaders();
    await axios.delete(`${API_URL}/notifications/subscription`, { headers });
  },

  async getHistory(limit = 50): Promise<NotificationHistoryItem[]> {
    const headers = await authHeaders();
    const { data } = await axios.get(`${API_URL}/notifications/history`, { headers, params: { limit } });
    return data.data || [];
  },

  async sendTest(): Promise<TestResult> {
    const headers = await authHeaders();
    try {
      await axios.post(`${API_URL}/notifications/test`, {}, { headers });
      return 'enviado';
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.error;
      if (status === 503 && code === 'envio_inactivo') return 'envio_inactivo';
      if (status === 404 && /subscription/i.test(code || '')) return 'sin_suscripcion';
      if (status === 404) return 'sin_documentos';
      return 'error';
    }
  },
};
